
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchArticles } from '../services/api';
import { adaptArticlesData, isValidArticle } from '../services/articleAdapter';

// Cache en mémoire pour éviter les requêtes répétées
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useArticles = (page = 1, limit = 10, categorySlug = '', featured = false, sort = '') => {
  const [data, setData] = useState({ articles: [], totalPages: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Mémoriser la clé de cache pour éviter les recalculs
  const cacheKey = useMemo(() => {
    return `${page}-${limit}-${categorySlug}-${featured}-${sort}`;
  }, [page, limit, categorySlug, featured, sort]);

  // Vérifier le cache avant de faire la requête
  const getCachedData = useCallback((key) => {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, []);

  // Mettre en cache les données
  const setCachedData = useCallback((key, data) => {
    cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }, []);

  const fetchWithRetry = useCallback(async (retries = 1) => { // Réduire les tentatives
    // Vérifier le cache d'abord
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const params = {
      page: typeof page === 'number' && page > 0 ? page : 1,
      limit: typeof limit === 'number' && limit > 0 ? limit : 10,
      featured,
    };

    if (categorySlug && typeof categorySlug === 'string' && categorySlug.trim() !== '') {
      params.categorySlug = categorySlug.trim();
    }

    if (sort && typeof sort === 'string' && sort.trim() !== '') {
      params.sort = sort.trim();
    }

    for (let i = 0; i <= retries; i++) {
      try {
        const response = await fetchArticles(params);
        // Mettre en cache la réponse
        setCachedData(cacheKey, response);
        return response;
      } catch (err) {
        if (i === retries) throw err;
        await new Promise(resolve => setTimeout(resolve, 500 * (i + 1))); // Réduire le délai
      }
    }
  }, [page, limit, categorySlug, featured, sort, cacheKey, getCachedData, setCachedData]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        setError(null);
        
        // Vérifier le cache immédiatement
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          const adaptedArticles = adaptArticlesData(cachedData.articles || []);
          const validArticles = adaptedArticles.filter(isValidArticle);
          
          setData({
            articles: validArticles,
            totalPages: cachedData.totalPages || 0,
            total: cachedData.total || validArticles.length,
          });
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        const response = await fetchWithRetry();

        if (isMounted.current && !abortController.signal.aborted) {
          if (!response) throw new Error('Aucune réponse reçue du serveur');

          const adaptedArticles = adaptArticlesData(response.articles || []);
          const validArticles = adaptedArticles.filter(isValidArticle);

          setData({
            articles: validArticles,
            totalPages: response.totalPages || 0,
            total: response.total || validArticles.length,
          });
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (isMounted.current) {
          console.error('Erreur chargement articles:', err);
          setError(`Erreur: ${err.message}`);
        }
      } finally {
        if (isMounted.current && !abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted.current = false;
      abortController.abort();
    };
  }, [cacheKey, fetchWithRetry, getCachedData]);

  const retry = useCallback(() => {
    // Vider le cache pour cette clé lors d'un retry
    cache.delete(cacheKey);
    setError(null);
    setIsLoading(true);
    // Déclencher un nouveau fetch
    window.location.reload();
  }, [cacheKey]);

  return { data, isLoading, error, retry };
};